import { Component, EventEmitter, Input, Output } from '@angular/core';
import { finalize } from 'rxjs';
import { partnerBrandingFor } from '../../models/integration/partner-branding';
import { PARTNER_SCOPE_OPTIONS } from '../../models/outbound/partner-client.model';
import { IPartnerConnection } from '../../models/outbound/partner-connection.model';
import { PartnerConnectionService } from '../../services/partner-connection.service';

/** §2 step 2: the issuer identity aumico sends as a non-secret hint. */
const AUMICO_ISSUER = 'https://aumico.io';

/**
 * Info modal of a guided partner card (REV-21656). Follows the house pattern:
 * the card carries no actions — connect and disconnect live HERE. Connect only
 * forwards to the partner's registered start URL with non-secret hints; the
 * OAuth flow itself runs on /oauth/authorize when the partner comes back.
 */
@Component({
    selector: 'aumico-partner-connect-modal',
    templateUrl: './partner-connect-modal.component.html',
    styleUrls: ['./partner-connect-modal.component.scss'],
})
export class PartnerConnectModalComponent {
    @Input() public connection!: IPartnerConnection;

    /** The signed-in admin's email, forwarded as login_hint (never a secret). */
    @Input() public loginHint: string | null = null;

    @Output() public closed = new EventEmitter<void>();
    @Output() public disconnected = new EventEmitter<void>();

    public view: 'detail' | 'confirm_disconnect' = 'detail';
    public busy = false;
    public hasError = false;

    public constructor(private readonly partnerConnectionService: PartnerConnectionService) {}

    /** Partner-specific modal wording where branded; generic text otherwise. */
    public get modalTextKey(): string {
        return partnerBrandingFor(this.connection.appKey).modalTextKey ?? 'aumico_partner_connect_modal_text';
    }

    public get scopeLabelKeys(): string[] {
        return PARTNER_SCOPE_OPTIONS.filter((option) => this.connection.scopes.includes(option.scope)).map(
            (option) => option.labelKey,
        );
    }

    public onClose(): void {
        this.closed.emit();
    }

    public onConnect(): void {
        if (this.connection.startUrl == null) {
            return;
        }
        const target = new URL(this.connection.startUrl);
        target.searchParams.set('iss', AUMICO_ISSUER);
        if (this.loginHint != null) {
            target.searchParams.set('login_hint', this.loginHint);
        }
        window.location.assign(target.toString());
    }

    public onDisconnectRequested(): void {
        this.view = 'confirm_disconnect';
    }

    public onBackToDetail(): void {
        this.view = 'detail';
    }

    public onDisconnectConfirmed(): void {
        if (this.busy) {
            return;
        }
        this.busy = true;
        this.hasError = false;
        this.partnerConnectionService
            .disconnect(this.connection.clientId)
            .pipe(finalize(() => (this.busy = false)))
            .subscribe({
                next: () => this.disconnected.emit(),
                error: () => (this.hasError = true),
            });
    }
}
